import { normalizaAlias } from '../normalizacion/normalizaAlias.js'
import { resolverValorDetallado } from '../motor/parser.js'

const REPITE_RE = /\$\{repiteEstructura:([^\}]+)\}/g
const PLACEHOLDER_RE = /\$\{([^}]+)\}/g

function registrarError(errors, code, message, detail) {
  errors.push({ code, message, detail })
}

function stripWrapper(str) {
  let s = (str || '').trim()
  if (s.startsWith('${') && s.endsWith('}')) s = s.slice(2, -1).trim()
  return s
}

function splitArgs(str) {
  const args = []
  let current = ''
  let inQuotes = false
  let escape = false
  let parenDepth = 0

  for (let i = 0; i < str.length; i++) {
    const c = str[i]

    if (escape) {
      current += c
      escape = false
      continue
    }

    if (c === '\\' && inQuotes) {
      current += c
      escape = true
      continue
    }

    if (c === '"') {
      current += c
      inQuotes = !inQuotes
      continue
    }

    if (!inQuotes && c === '(') {
      parenDepth++
      current += c
      continue
    }

    if (!inQuotes && c === ')') {
      parenDepth = Math.max(0, parenDepth - 1)
      current += c
      continue
    }

    if (!inQuotes && parenDepth === 0 && c === ',') {
      args.push(current.trim())
      current = ''
      continue
    }

    current += c
  }

  if (current.trim().length > 0) args.push(current.trim())
  return args
}

function splitTopLevel(str, separador) {
  const partes = []
  let current = ''
  let inQuotes = false
  let escape = false
  let parenDepth = 0

  for (let i = 0; i < str.length; i++) {
    const c = str[i]

    if (escape) {
      current += c
      escape = false
      continue
    }

    if (c === '\\' && inQuotes) {
      current += c
      escape = true
      continue
    }

    if (c === '"') {
      current += c
      inQuotes = !inQuotes
      continue
    }

    if (!inQuotes && c === '(') {
      parenDepth++
      current += c
      continue
    }

    if (!inQuotes && c === ')') {
      parenDepth = Math.max(0, parenDepth - 1)
      current += c
      continue
    }

    if (!inQuotes && parenDepth === 0 && c === separador) {
      partes.push(current.trim())
      current = ''
      continue
    }

    current += c
  }

  if (current.trim().length > 0) partes.push(current.trim())
  return partes
}

function findTopLevelChar(str, char) {
  let inQuotes = false
  let escape = false
  let parenDepth = 0

  for (let i = 0; i < str.length; i++) {
    const c = str[i]

    if (escape) {
      escape = false
      continue
    }

    if (c === '\\' && inQuotes) {
      escape = true
      continue
    }

    if (c === '"') {
      inQuotes = !inQuotes
      continue
    }

    if (!inQuotes && c === '(') parenDepth++
    if (!inQuotes && c === ')') parenDepth = Math.max(0, parenDepth - 1)

    if (!inQuotes && parenDepth === 0 && c === char) return i
  }

  return -1
}

function listRows(xml) {
  const rows = []
  let pos = 0

  while (true) {
    const start = xml.indexOf('<w:tr', pos)
    if (start === -1) break

    const end = xml.indexOf('</w:tr>', start)
    if (end === -1) break

    const endPos = end + '</w:tr>'.length
    rows.push({ start, end: endPos, xml: xml.slice(start, endPos) })
    pos = endPos
  }

  return rows
}

function resolveDriverAlias(contenido, aliasToExpresion) {
  const key = contenido.trim()
  const exp = aliasToExpresion[key]?.expresion
  if (!exp) return null

  const raw = stripWrapper(exp)
  if (raw.startsWith('repiteEstructura:') || raw.startsWith('celdav2:') || raw.startsWith('contador:')) {
    return raw
  }

  return null
}

function expandAliasesForDriver(rowXml, aliasToExpresion) {
  return rowXml.replace(PLACEHOLDER_RE, (match, contenido) => {
    const exp = resolveDriverAlias(contenido, aliasToExpresion)
    if (!exp) return match
    return '${' + exp + '}'
  })
}

function parseRepiteArgs(raw) {
  const args = splitArgs(raw)
  const identificador = limpiarIdentificadorControl(args[0])
  const ejecucion = (args[1] || '').trim()
  const nodoIteracion = (args[2] || '').trim()
  const nodosSoporte = args.slice(3).map((arg) => arg.trim()).filter(Boolean)
  return { identificador, ejecucion, nodoIteracion, nodosSoporte }
}

function parseInicioSpec(raw) {
  const s = String(raw || '').trim()
  if (!s) return { modo: '', excluyente: false, condiciones: [] }

  const partes = splitTopLevel(s, '?')
  const modoRaw = limpiarIdentificadorControl(partes[0]).toLowerCase()
  const excluyente = modoRaw === 'inicioe'
  const modo = excluyente ? 'inicio' : modoRaw
  const condiciones = partes.slice(1).map((condicion) => parseCondicion(condicion)).filter(Boolean)
  return { modo, excluyente, condiciones }
}

function parseCondicion(raw) {
  const s = String(raw || '').trim()
  if (!s) return null

  const pos = findTopLevelChar(s, '@')
  if (pos === -1) {
    return {
      error: true,
      raw: s,
      message: 'Condición inválida en repiteEstructura',
    }
  }

  const campo = s.slice(0, pos).trim()
  const valor = s.slice(pos + 1).trim()
  if (!campo || !valor) {
    return {
      error: true,
      raw: s,
      message: 'Condición inválida en repiteEstructura',
    }
  }

  return { campo, valor, raw: s }
}

function normalizePathArg(arg, aliasToVariable, aliasToExpresion) {
  if (!arg) return ''

  let limpio = arg.trim()
  limpio = limpio.replace(/\[\*\]$/, '').replace(/\[\]$/, '')

  if (aliasToVariable[limpio]?.fullPath) return aliasToVariable[limpio].fullPath

  if (aliasToExpresion[limpio]?.expresion) {
    const raw = stripWrapper(aliasToExpresion[limpio].expresion)
    const m = raw.match(/^(v:|i:)(.+)$/)
    if (m) return m[2].trim()
  }

  const m = limpio.match(/^(v:|i:)(.+)$/)
  if (m) return m[2].trim()

  return limpio
}

function pathWithIndex(basePath, idx) {
  if (!basePath) return ''
  if (basePath.includes('[*]')) return basePath.replace('[*]', `[${idx}]`)
  if (basePath.includes('[]')) return basePath.replace('[]', `[${idx}]`)
  return `${basePath}[${idx}]`
}

function limpiarSegmento(valor) {
  return String(valor || '')
    .trim()
    .replace(/\[\*\]$/, '')
    .replace(/\[\]$/, '')
}

function extraerLiteralDoble(raw) {
  const s = String(raw || '').trim()
  if (s.length < 2) return null
  if (!s.startsWith('"') || !s.endsWith('"')) return null
  return s.slice(1, -1).replace(/\\"/g, '"')
}

function limpiarIdentificadorControl(raw) {
  const literal = extraerLiteralDoble(raw)
  return literal != null ? literal : String(raw || '').trim()
}

function escapeRegExp(str) {
  return String(str || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function relativizarCampo(fullPath, basePath) {
  if (!fullPath) return ''
  if (!basePath) return fullPath

  const base = String(basePath || '').trim().replace(/\[\*\]$/, '').replace(/\[\]$/, '')
  const limpio = String(fullPath || '').trim()
  if (limpio === base || limpio === basePath) return ''

  const regex = new RegExp(`^${escapeRegExp(base)}(?:\\[(?:\\*|\\d+)\\]|\\[\\])?(?:\\.)?`)
  if (regex.test(limpio)) return limpio.replace(regex, '')

  return fullPath
}

function normalizarCampoCondicion(campo, basePath, aliasToVariable, aliasToExpresion) {
  const raw = String(campo || '').trim()
  if (!raw) return ''

  if (aliasToVariable[raw]?.fullPath) {
    return relativizarCampo(aliasToVariable[raw].fullPath, basePath)
  }

  if (aliasToExpresion[raw]?.expresion) {
    const expRaw = stripWrapper(aliasToExpresion[raw].expresion)
    const match = expRaw.match(/^(v:|i:)(.+)$/)
    if (match) {
      return relativizarCampo(match[2].trim(), basePath)
    }
  }

  const match = raw.match(/^(v:|i:)(.+)$/)
  if (match) {
    return relativizarCampo(match[2].trim(), basePath)
  }

  return relativizarCampo(raw, basePath)
}

function resolverValorCondicion(raw, datos, aliasToVariable, aliasToExpresion, errors) {
  const literal = extraerLiteralDoble(raw)
  if (literal != null) return literal

  const limpio = String(raw || '').trim()
  if (!limpio) return ''

  if (/^-?(?:\d+|\d*\.\d+)$/.test(limpio)) {
    return limpio
  }

  const aliasVariable = aliasToVariable[limpio]
  if (aliasVariable?.fullPath) {
    const res = resolverValorDetallado(datos, aliasVariable.fullPath)
    if (!res.ok) {
      errors.push(res.error)
      return ''
    }
    return res.value
  }

  const aliasExpresion = aliasToExpresion[limpio]
  if (aliasExpresion?.expresion) {
    const expRaw = stripWrapper(aliasExpresion.expresion)
    const match = expRaw.match(/^(v:|i:)(.+)$/)
    if (match) {
      const res = resolverValorDetallado(datos, match[2].trim())
      if (!res.ok) {
        errors.push(res.error)
        return ''
      }
      return res.value
    }
    registrarError(
      errors,
      'repite_estructura_condicion_no_resoluble',
      'La condición de repiteEstructura no puede resolver un alias de expresión',
      limpio
    )
    return ''
  }

  const match = limpio.match(/^(v:|i:)(.+)$/)
  if (match) {
    const res = resolverValorDetallado(datos, match[2].trim())
    if (!res.ok) {
      errors.push(res.error)
      return ''
    }
    return res.value
  }

  if (/[.\[]/.test(limpio)) {
    const res = resolverValorDetallado(datos, limpio)
    if (!res.ok) {
      errors.push(res.error)
      return ''
    }
    return res.value
  }

  registrarError(
    errors,
    'repite_estructura_condicion_valor_invalid',
    'El valor de una condición de repiteEstructura debe ser literal entre comillas, número, alias o ruta',
    limpio
  )
  return ''
}

function normalizarComparable(valor) {
  if (valor == null) return ''
  if (typeof valor === 'object') return JSON.stringify(valor)
  return String(valor)
}

function cumpleCondicionesItem(item, bloqueActivo, datos, aliasToVariable, aliasToExpresion, errors) {
  const condiciones = bloqueActivo.condiciones || []
  if (!condiciones.length) return true

  let coincideAlguna = false
  for (const condicion of condiciones) {
    if (condicion?.error) {
      registrarError(
        errors,
        'repite_estructura_condicion_invalid',
        condicion.message || 'Condición inválida en repiteEstructura',
        condicion.raw
      )
      return false
    }

    const campoRelativo = normalizarCampoCondicion(
      condicion.campo,
      bloqueActivo.baseIteracion,
      aliasToVariable,
      aliasToExpresion
    )
    if (!campoRelativo && campoRelativo !== '') {
      registrarError(
        errors,
        'repite_estructura_condicion_invalid',
        'No se pudo normalizar el campo de una condición de repiteEstructura',
        condicion.campo
      )
      return false
    }

    let valorCampo = item
    if (campoRelativo) {
      const resCampo = resolverValorDetallado(item, campoRelativo)
      if (!resCampo.ok) {
        errors.push(resCampo.error)
        return false
      }
      valorCampo = resCampo.value
    }

    const valorEsperado = resolverValorCondicion(
      condicion.valor,
      datos,
      aliasToVariable,
      aliasToExpresion,
      errors
    )

    if (normalizarComparable(valorCampo) === normalizarComparable(valorEsperado)) {
      coincideAlguna = true
      break
    }
  }

  return bloqueActivo.excluyente ? !coincideAlguna : coincideAlguna
}

function obtenerClaveNodo(ruta) {
  const limpia = limpiarSegmento(ruta)
  if (!limpia) return ''
  const partes = limpia.split('.').filter(Boolean)
  return limpiarSegmento(partes[partes.length - 1] || limpia)
}

function existeRuta(datos, ruta) {
  if (!datos || !ruta) return false
  const resultado = resolverValorDetallado(datos, ruta)
  return resultado.ok && !resultado.missing
}

function resolverRutaCeldav2(campo, bloqueActivo, idx, datos) {
  const limpio = String(campo || '').trim().replace(/^(v:|i:)/, '')
  if (!limpio) return ''

  const partes = limpio.split('.').filter(Boolean)
  const claveCampo = limpiarSegmento(partes[0])
  const restoCampo = partes.slice(1).join('.')

  const rutas = bloqueActivo.rutasIndice || []
  const rutaIteracion = rutas.find((ruta) => ruta?.basePath && ruta.esIteracion)
  const baseIteracion = rutaIteracion?.basePath || bloqueActivo.baseIteracion
  if (!baseIteracion) return ''

  const baseConIndice = pathWithIndex(baseIteracion, idx)
  const claveIteracion = limpiarSegmento(rutaIteracion?.clave || obtenerClaveNodo(baseIteracion))
  const esRutaIteracion =
    limpio === baseIteracion ||
    limpio.startsWith(`${baseIteracion}.`) ||
    limpio.startsWith(`${baseIteracion}[*]`) ||
    limpio.startsWith(`${baseIteracion}[]`) ||
    (claveIteracion &&
      (limpio === claveIteracion ||
        limpio.startsWith(`${claveIteracion}.`) ||
        limpio.startsWith(`${claveIteracion}[*]`) ||
        limpio.startsWith(`${claveIteracion}[]`)))

  if (esRutaIteracion) {
    if (limpio === baseIteracion || limpio === claveIteracion) return baseConIndice
    if (limpio.startsWith(`${baseIteracion}.`)) {
      const tail = limpio.slice(baseIteracion.length).replace(/^\./, '')
      return tail ? `${baseConIndice}.${tail}` : baseConIndice
    }
    if (limpio.startsWith(`${claveIteracion}.`)) {
      const tail = limpio.slice(claveIteracion.length).replace(/^\./, '')
      return tail ? `${baseConIndice}.${tail}` : baseConIndice
    }
    return limpio
      .replace(`${baseIteracion}[*]`, baseConIndice)
      .replace(`${baseIteracion}[]`, baseConIndice)
      .replace(`${claveIteracion}[*]`, baseConIndice)
      .replace(`${claveIteracion}[]`, baseConIndice)
  }

  const rutaSoporte = rutas.find((ruta) => {
    if (!ruta?.basePath || ruta.esIteracion) return false
    if (limpio === ruta.basePath || limpio.startsWith(`${ruta.basePath}.`)) return true
    return ruta.clave && (ruta.clave === claveCampo || limpio.startsWith(`${ruta.clave}.`))
  })

  if (rutaSoporte) {
    const base = rutaSoporte.basePath
    if (limpio === base) return base
    if (limpio.startsWith(`${base}.`)) return limpio
    return restoCampo ? `${base}.${restoCampo}` : base
  }

  const rutaSoporteConCampo = rutas.find((ruta) => {
    if (!ruta?.basePath || ruta.esIteracion) return false
    if (/\[\*\]|\[\]/.test(limpio)) return false
    return existeRuta(datos, `${ruta.basePath}.${limpio}`)
  })

  if (rutaSoporteConCampo) {
    return `${rutaSoporteConCampo.basePath}.${limpio}`
  }
  return `${baseConIndice}.${limpio}`
}

function replacePathOutsideQuotes(str, token, replacement) {
  if (!str || !token) return str

  let out = ''
  let inQuotes = false
  let escape = false

  for (let i = 0; i < str.length; i++) {
    const c = str[i]

    if (escape) {
      out += c
      escape = false
      continue
    }

    if (c === '\\' && inQuotes) {
      out += c
      escape = true
      continue
    }

    if (c === '"') {
      out += c
      inQuotes = !inQuotes
      continue
    }

    if (!inQuotes && str.startsWith(token, i)) {
      const prev = i > 0 ? str[i - 1] : ''
      const next = i + token.length < str.length ? str[i + token.length] : ''
      const prevOk = prev !== '.' && !/[A-Za-z0-9_]/.test(prev)
      const nextOk = !/[A-Za-z0-9_]/.test(next)
      if (prevOk && nextOk) {
        out += replacement
        i += token.length - 1
        continue
      }
    }

    out += c
  }

  return out
}

function replaceBarePathOutsideQuotes(str, token, replacement) {
  if (!str || !token) return str

  let out = ''
  let inQuotes = false
  let escape = false

  for (let i = 0; i < str.length; i++) {
    const c = str[i]

    if (escape) {
      out += c
      escape = false
      continue
    }

    if (c === '\\' && inQuotes) {
      out += c
      escape = true
      continue
    }

    if (c === '"') {
      out += c
      inQuotes = !inQuotes
      continue
    }

    if (!inQuotes && str.startsWith(token, i)) {
      const prev = i > 0 ? str[i - 1] : ''
      const next = i + token.length < str.length ? str[i + token.length] : ''
      const prevOk = prev !== '.' && !/[A-Za-z0-9_]/.test(prev)
      const nextOk = next !== '[' && !/[A-Za-z0-9_]/.test(next)
      if (prevOk && nextOk) {
        out += replacement
        i += token.length - 1
        continue
      }
    }

    out += c
  }

  return out
}

function replaceAliasOutsideQuotes(str, alias, replacement) {
  if (!str || !alias || !replacement) return str

  let out = ''
  let inQuotes = false
  let escape = false

  for (let i = 0; i < str.length; i++) {
    const c = str[i]

    if (escape) {
      out += c
      escape = false
      continue
    }

    if (c === '\\' && inQuotes) {
      out += c
      escape = true
      continue
    }

    if (c === '"') {
      out += c
      inQuotes = !inQuotes
      continue
    }

    if (!inQuotes && str.startsWith(alias, i)) {
      const prev = i > 0 ? str[i - 1] : ''
      const next = i + alias.length < str.length ? str[i + alias.length] : ''
      const prevOk = prev !== '.' && !/[A-Za-z0-9_]/.test(prev)
      const nextOk = !/[A-Za-z0-9_]/.test(next) && next !== ':'
      if (prevOk && nextOk) {
        const prefijoPrevio = prev === ':' ? str[i - 2] : ''
        const reemplazo =
          (prefijoPrevio === 'v' || prefijoPrevio === 'i')
            ? replacement.replace(/^(v:|i:)/, '')
            : replacement
        out += reemplazo
        i += alias.length - 1
        continue
      }
    }

    out += c
  }

  return out
}

function expresionAliasRuta(alias, aliasToVariable, aliasToExpresion) {
  const variable = aliasToVariable?.[alias]
  if (variable?.fullPath) return `v:${variable.fullPath}`

  const expresion = aliasToExpresion?.[alias]?.expresion
  if (!expresion) return ''

  const raw = stripWrapper(expresion)
  if (raw.startsWith('repiteEstructura:') || raw.startsWith('celdav2:') || raw.startsWith('contador:')) return ''
  if (/^(v:|i:)/.test(raw)) return raw
  if (/[.\[]/.test(raw) && !raw.includes(':')) return `v:${raw}`
  if (raw.includes(':')) return `(${raw})`
  return raw
}

function expandirAliasesRutaEnContenido(contenido, aliasToVariable, aliasToExpresion) {
  let nuevo = contenido
  let iteraciones = 0

  do {
    const anterior = nuevo

    for (const alias of Object.keys(aliasToVariable || {})) {
      const reemplazo = expresionAliasRuta(alias, aliasToVariable, aliasToExpresion)
      nuevo = replaceAliasOutsideQuotes(nuevo, alias, reemplazo)
    }

    for (const alias of Object.keys(aliasToExpresion || {})) {
      const reemplazo = expresionAliasRuta(alias, aliasToVariable, aliasToExpresion)
      nuevo = replaceAliasOutsideQuotes(nuevo, alias, reemplazo)
    }

    iteraciones++
    if (nuevo === anterior) break
  } while (iteraciones < 5)

  return nuevo
}

function aplicarRutasIndiceEnContenido(contenido, rutas, idx) {
  let nuevo = contenido
  for (const ruta of rutas) {
    if (!ruta?.basePath || !ruta?.aplicaIndice) continue
    const indexed = pathWithIndex(ruta.basePath, idx)
    nuevo = replacePathOutsideQuotes(nuevo, `${ruta.basePath}[*]`, indexed)
    nuevo = replacePathOutsideQuotes(nuevo, `${ruta.basePath}[]`, indexed)
    nuevo = replaceBarePathOutsideQuotes(nuevo, ruta.basePath, indexed)

    if (ruta.clave && ruta.clave !== ruta.basePath) {
      nuevo = replacePathOutsideQuotes(nuevo, `${ruta.clave}[*]`, indexed)
      nuevo = replacePathOutsideQuotes(nuevo, `${ruta.clave}[]`, indexed)
      nuevo = replaceBarePathOutsideQuotes(nuevo, ruta.clave, indexed)
    }
  }
  return nuevo
}

function aplicarIndicesEnBloque(rowXml, bloqueActivo, idx, errors, idxContador = idx) {
  const rutas = bloqueActivo.rutasIndice || []
  if (!rutas.length) return rowXml

  return rowXml.replace(PLACEHOLDER_RE, (match, contenido) => {
    const contenidoDirectiva = resolveDriverAlias(contenido, bloqueActivo.aliasToExpresion) || contenido

    if (contenidoDirectiva.startsWith('contador:')) {
      const args = splitArgs(contenidoDirectiva.slice('contador:'.length))
      const identificador = limpiarIdentificadorControl(args[0])
      const inicio = (args[1] || '').trim()
      const paso = (args[2] || '').trim()

      if (!identificador) {
        registrarError(
          errors,
          'contador_args_invalid',
          'contador sin identificador de tabla o bloque',
          contenidoDirectiva
        )
        return match
      }

      if (identificador !== bloqueActivo.identificador) {
        registrarError(
          errors,
          'contador_block_mismatch',
          'contador apunta a un bloque distinto al que se está expandiendo',
          `${identificador} !== ${bloqueActivo.identificador}`
        )
        return match
      }

      return '${contador:"' + identificador.replace(/"/g, '\\"') + '",' + inicio + ',' + paso + ',' + idxContador + '}'
    }

    if (contenidoDirectiva.startsWith('celdav2:')) {
      const args = splitArgs(contenidoDirectiva.slice('celdav2:'.length))
      const identificador = limpiarIdentificadorControl(args[0])
      const campo = (args[1] || '').trim()
      const expr = args[2] != null ? args.slice(2).join(',').trim() : ''
      if (!identificador) {
        registrarError(
          errors,
          'celdav2_args_invalid',
          'celdav2 sin identificador',
          contenidoDirectiva
        )
        return match
      }

      if (identificador !== bloqueActivo.identificador) {
        registrarError(
          errors,
          'celdav2_block_mismatch',
          'celdav2 apunta a un bloque distinto al que se está expandiendo',
          `${identificador} !== ${bloqueActivo.identificador}`
        )
        return match
      }

      if (!campo) {
        registrarError(
          errors,
          'celdav2_args_invalid',
          'celdav2 sin campo',
          contenidoDirectiva
        )
        return match
      }

      const ruta = resolverRutaCeldav2(campo, bloqueActivo, idx, bloqueActivo.datos)
      if (!ruta) {
        registrarError(
          errors,
          'celdav2_path_invalid',
          'No se pudo resolver la ruta de celdav2',
          campo
        )
        return match
      }

      const baseExpr = `v:${ruta}`
      if (!expr) return '${' + baseExpr + '}'

      const exprRaw = stripWrapper(expr)
      const exprConBase = replacePathOutsideQuotes(exprRaw, '@v', baseExpr)
      const exprConAliases = expandirAliasesRutaEnContenido(
        exprConBase,
        bloqueActivo.aliasToVariable,
        bloqueActivo.aliasToExpresion
      )
      const exprConIndices = aplicarRutasIndiceEnContenido(exprConAliases, rutas, idx)
      return '${' + exprConIndices + '}'
    }

    const nuevo = aplicarRutasIndiceEnContenido(contenidoDirectiva, rutas, idx)
    return '${' + nuevo + '}'
  })
}

function quitarMarcadorExacto(rowXml, marcador) {
  if (!rowXml || !marcador) return rowXml
  return rowXml.replace(marcador, '')
}

function construirPiezaBloque(bloqueActivo) {
  const filas = bloqueActivo.filas || []
  if (!filas.length) return ''

  const ultima = filas.length - 1
  return filas
    .map((filaXml, idx) => {
      let pieza = filaXml
      if (idx === 0) pieza = quitarMarcadorExacto(pieza, bloqueActivo.inicioRaw)
      if (idx === ultima) pieza = quitarMarcadorExacto(pieza, bloqueActivo.finRaw)
      return pieza
    })
    .join('')
}

function expandirBloqueActivo(bloqueActivo, datos, errors) {
  const baseValue = resolverValorDetallado(datos, bloqueActivo.baseIteracion)
  if (!baseValue.ok) {
    errors.push(baseValue.error)
    return bloqueActivo.filas.join('')
  }

  const elementos = baseValue.missing ? [] : baseValue.value

  if (!Array.isArray(elementos)) {
    registrarError(
      errors,
      'repite_estructura_base_not_array',
      'El nodo de iteración de repiteEstructura no resuelve a un array',
      `${bloqueActivo.identificador}: ${bloqueActivo.baseIteracion}`
    )
    return bloqueActivo.filas.join('')
  }

  let bloque = ''
  let contadorIdx = 0
  for (let i = 0; i < elementos.length; i++) {
    const item = elementos[i]
    if (!cumpleCondicionesItem(item, bloqueActivo, datos, bloqueActivo.aliasToVariable, bloqueActivo.aliasToExpresion, errors)) {
      continue
    }

    let pieza = construirPiezaBloque(bloqueActivo)
    pieza = aplicarIndicesEnBloque(pieza, bloqueActivo, i, errors, contadorIdx)
    bloque += pieza
    contadorIdx++
  }

  return bloque
}

function expandirUnaVez(xml, datos, variables, expresiones, errors) {
  const { aliasToVariable, aliasToExpresion } = normalizaAlias(variables, expresiones)
  const rows = listRows(xml)
  if (!rows.length) return xml

  let out = ''
  let cursor = 0
  let bloqueActivo = null

  for (const row of rows) {
    if (cursor < row.start && !bloqueActivo) {
      out += xml.slice(cursor, row.start)
      cursor = row.start
    }

    const rowXml = expandAliasesForDriver(row.xml, aliasToExpresion)
    const matches = []
    let m
    REPITE_RE.lastIndex = 0
    while ((m = REPITE_RE.exec(rowXml)) !== null) {
      matches.push({ index: m.index, raw: m[0], contenido: m[1] })
    }
    REPITE_RE.lastIndex = 0

    const startMatch = matches.find((item) => {
      const args = parseRepiteArgs(item.contenido)
      return parseInicioSpec(args.ejecucion).modo === 'inicio'
    })
    const endMatch = matches.find((item) => {
      const args = parseRepiteArgs(item.contenido)
      const spec = parseInicioSpec(args.ejecucion)
      return spec.modo === 'fin' && !spec.condiciones.length
    })

    if (!bloqueActivo) {
      if (!startMatch) {
        if (endMatch) {
          registrarError(
            errors,
            'repite_estructura_inicio_missing',
            'repiteEstructura fin sin inicio asociado',
            endMatch.contenido
          )
        }
        out += rowXml
        cursor = row.end
        continue
      }

      const inicio = parseRepiteArgs(startMatch.contenido)
      if (!inicio.identificador || !inicio.nodoIteracion) {
        registrarError(
          errors,
          'repite_estructura_args_invalid',
          'repiteEstructura sin args válidos en inicio',
          startMatch.contenido
        )
        out += rowXml
        cursor = row.end
        continue
      }

      const inicioSpec = parseInicioSpec(inicio.ejecucion)
      if (inicioSpec.modo !== 'inicio') {
        registrarError(
          errors,
          'repite_estructura_inicio_invalid',
          'repiteEstructura inicio inválido',
          inicio.ejecucion
        )
        out += rowXml
        cursor = row.end
        continue
      }

      const baseIteracion = normalizePathArg(inicio.nodoIteracion, aliasToVariable, aliasToExpresion)

      bloqueActivo = {
        identificador: inicio.identificador,
        baseIteracion,
        excluyente: inicioSpec.excluyente,
        condiciones: inicioSpec.condiciones,
        aliasToVariable,
        aliasToExpresion,
        datos,
        inicioRaw: startMatch.raw,
        finRaw: null,
        rutasIndice: [
          {
            basePath: baseIteracion,
            aplicaIndice: true,
            esIteracion: true,
            clave: limpiarSegmento(obtenerClaveNodo(baseIteracion)),
          },
          ...inicio.nodosSoporte.map((arg) => {
            const basePath = normalizePathArg(arg, aliasToVariable, aliasToExpresion)
            return {
              basePath,
              aplicaIndice: /\[\*\]|\[\]$/.test(String(arg || '').trim()),
              esIteracion: false,
              clave: limpiarSegmento(obtenerClaveNodo(basePath)),
            }
          }),
        ].filter((ruta) => ruta.basePath),
        filas: [rowXml],
      }
      cursor = row.end

      if (endMatch) {
        const fin = parseRepiteArgs(endMatch.contenido)
        const finSpec = parseInicioSpec(fin.ejecucion)
        if (fin.identificador === bloqueActivo.identificador && finSpec.modo === 'fin' && !finSpec.condiciones.length) {
          bloqueActivo.finRaw = endMatch.raw
          out += expandirBloqueActivo(bloqueActivo, datos, errors)
          bloqueActivo = null
        } else {
          registrarError(
            errors,
            'repite_estructura_fin_mismatch',
            'repiteEstructura fin no coincide con el bloque activo',
            `${fin.identificador || '(sin identificador)'} !== ${bloqueActivo.identificador}`
          )
        }
      }

      continue
    }

    bloqueActivo.filas.push(rowXml)

    if (!endMatch) {
      cursor = row.end
      continue
    }

    const fin = parseRepiteArgs(endMatch.contenido)
    const finSpec = parseInicioSpec(fin.ejecucion)
    if (fin.identificador !== bloqueActivo.identificador || finSpec.modo !== 'fin' || finSpec.condiciones.length) {
      registrarError(
        errors,
        'repite_estructura_fin_mismatch',
        'repiteEstructura fin no coincide con el bloque activo',
        `${fin.identificador || '(sin identificador)'} !== ${bloqueActivo.identificador}`
      )
      cursor = row.end
      continue
    }

    bloqueActivo.finRaw = endMatch.raw
    out += expandirBloqueActivo(bloqueActivo, datos, errors)
    bloqueActivo = null
    cursor = row.end
  }

  if (bloqueActivo) {
    registrarError(
      errors,
      'repite_estructura_fin_missing',
      'repiteEstructura inicio sin fin asociado',
      bloqueActivo.identificador
    )
    out += bloqueActivo.filas.join('')
  }

  if (cursor < xml.length) {
    out += xml.slice(cursor)
  }

  return out
}

function contieneRepiteEstructura(xml) {
  REPITE_RE.lastIndex = 0
  const tiene = REPITE_RE.test(xml)
  REPITE_RE.lastIndex = 0
  return tiene
}

export function expandirRepiteEstructura(xml, datos, variables = [], expresiones = [], errors = []) {
  let anterior = xml
  let actual = xml
  let iteraciones = 0

  do {
    anterior = actual
    actual = expandirUnaVez(anterior, datos, variables, expresiones, errors)
    iteraciones++
  } while (actual !== anterior && contieneRepiteEstructura(actual) && iteraciones < 5)

  if (actual !== anterior && contieneRepiteEstructura(actual) && iteraciones >= 5) {
    registrarError(
      errors,
      'repite_estructura_max_iterations',
      'repiteEstructura superó el máximo de iteraciones de preproceso',
      String(iteraciones)
    )
  }

  return actual
}
